// src/components/ETFCalculatorModal.js
//
// v3.5.99.7.13 — Early Termination Fee calculator as a modal overlay.
// Same calculator logic powers the standalone ETFCalculatorPage; only the
// chrome differs (modal here, page there). Both consume etfCalculator lib.
//
// Per-quote integration: the 5 quote pages each mount this and pass:
//   - defaultMRR        — from result.finalMRR
//   - defaultTerm       — from inputs.term
//   - defaultClientName — from recipientBiz
//   - defaultClientAddress — from recipientAddress
//   - quoteId           — for SIGNATURE_COMPLETED lookup (defaults contractStart)
//   - hubDealId         — to enable the "Post to HubSpot Deal" button
//
// Standalone integration: ETFCalculatorPage renders with no pre-fills and
// no quoteId/hubDealId — rep enters everything manually.

import { useState, useEffect, useMemo } from 'react';
import { supabase, logActivity } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { calculateEtf, buildEtfLetterHTML, buildEtfHubspotNoteBody, formatters } from '../lib/etfCalculator';
import { createHubspotNote } from '../lib/hubspot';
import { v3 } from '../lib/styleTokens';

export default function ETFCalculatorModal({
  onClose,
  defaultMRR = '',
  defaultTerm = 36,
  defaultClientName = '',
  defaultClientAddress = '',
  defaultClientContact = '',  // v7.14: contact NAME for SignWell signer
  defaultClientEmail = '',    // v7.14: contact EMAIL for SignWell signer
  defaultContractReference = '',  // v7.14.2: quote_number or human-readable contract id
  quoteId = null,
  hubDealId = null,
}) {
  const { profile } = useAuth();

  const [clientName,    setClientName]    = useState(defaultClientName);
  const [clientAddress, setClientAddress] = useState(defaultClientAddress);
  const [clientContact, setClientContact] = useState(defaultClientContact);
  const [clientEmail,   setClientEmail]   = useState(defaultClientEmail);
  const [contractReference, setContractReference] = useState(defaultContractReference);
  const [mrr,           setMrr]           = useState(defaultMRR);
  const [term,          setTerm]          = useState(defaultTerm);
  const [contractStart, setContractStart] = useState('');
  const [terminationDate, setTerminationDate] = useState(() => new Date().toISOString().slice(0, 10));

  // v7.14: ferrumInfo now includes logoUrl from pricing_settings.company_logo_url
  const [ferrumInfo, setFerrumInfo] = useState({ name: 'Ferrum Technology Services', address: '', phone: '', website: '', logoUrl: '' });
  const [postingNote, setPostingNote] = useState(false);
  const [postStatus,  setPostStatus]  = useState('');
  const [hintLoaded,  setHintLoaded]  = useState(false);
  // v7.14: independent state for PDF + SignWell actions
  const [generatingPdf,    setGeneratingPdf]    = useState(false);
  const [sendingSignwell,  setSendingSignwell]  = useState(false);
  const [pdfStatus,        setPdfStatus]        = useState('');
  const [signwellStatus,   setSignwellStatus]   = useState('');

  // ESC to dismiss — standard modal a11y
  useEffect(() => {
    function handleKey(e) { if (e.key === 'Escape') onClose?.(); }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // Fetch company info from pricing_settings (for the letter letterhead).
  // Falls back to sensible defaults if any field is missing.
  // v7.14: now includes company_logo_url for the new logo-in-header
  useEffect(() => {
    let cancelled = false;
    async function loadInfo() {
      const { data, error } = await supabase
        .from('pricing_settings')
        .select('key, value')
        .in('key', ['company_name', 'company_address', 'company_phone', 'company_website', 'company_logo_url']);
      if (cancelled || error) return;
      const map = {};
      for (const r of (data || [])) map[r.key] = r.value;
      setFerrumInfo({
        name:    map.company_name    || 'Ferrum Technology Services',
        address: map.company_address || '',
        phone:   map.company_phone   || '',
        website: map.company_website || '',
        logoUrl: map.company_logo_url || '',
      });
    }
    loadInfo();
    return () => { cancelled = true; };
  }, []);

  // If this modal opened from a quote, look up SIGNATURE_COMPLETED in the
  // activity log to suggest a contract start date. The rep can override.
  // We don't block on this — if no signature event, contractStart stays empty.
  useEffect(() => {
    if (!quoteId || contractStart) { setHintLoaded(true); return; }
    let cancelled = false;
    async function loadSignatureDate() {
      const { data } = await supabase
        .from('activity_log')
        .select('created_at')
        .eq('entity_id', quoteId)
        .eq('action', 'SIGNATURE_COMPLETED')
        .order('created_at', { ascending: false })
        .limit(1);
      if (cancelled) return;
      if (data && data[0]?.created_at) {
        setContractStart(data[0].created_at.slice(0, 10));
      }
      setHintLoaded(true);
    }
    loadSignatureDate();
    return () => { cancelled = true; };
  }, [quoteId, contractStart]);

  // Live calculation — recomputes on any field change.
  const calc = useMemo(() => {
    return calculateEtf({
      mrr,
      term,
      contractStart: contractStart ? new Date(contractStart) : null,
      terminationDate: terminationDate ? new Date(terminationDate) : null,
    });
  }, [mrr, term, contractStart, terminationDate]);

  const canCalculate = mrr && term && contractStart && terminationDate && calc.warnings.length === 0;

  async function handleGenerateLetter() {
    const html = buildEtfLetterHTML({
      clientName, clientAddress,
      mrr: Number(mrr), term: parseInt(term, 10),
      contractStart: new Date(contractStart),
      terminationDate: new Date(terminationDate),
      ferrumInfo,
      contractReference,
    });
    // Open in new window so rep can browser-print to PDF
    const w = window.open('', '_blank');
    if (!w) {
      window.alert('Could not open the letter — your browser may be blocking pop-ups. Allow pop-ups for this site and try again.');
      return;
    }
    w.document.write(html);
    w.document.close();

    // Audit trail
    if (quoteId) {
      await logActivity({
        action: 'ETF_CALCULATED',
        entityType: 'quote',
        entityId: quoteId,
        entityName: clientName,
        changes: {
          etf_amount:        { from: null, to: calc.etfAmount },
          months_remaining:  { from: null, to: calc.monthsRemaining },
          generated_letter:  { from: null, to: true },
        },
      });
    }
  }

  async function handlePostToHubspot() {
    if (!hubDealId) return;
    setPostingNote(true);
    setPostStatus('');
    try {
      const body = buildEtfHubspotNoteBody({
        clientName,
        mrr: Number(mrr), term: parseInt(term, 10),
        contractStart: new Date(contractStart),
        terminationDate: new Date(terminationDate),
        calculatedBy: profile?.full_name || profile?.email || 'Ferrum IQ user',
        contractReference,
      });
      await createHubspotNote(hubDealId, body);
      setPostStatus('✓ Posted to HubSpot deal');
      if (quoteId) {
        await logActivity({
          action: 'ETF_CALCULATED',
          entityType: 'quote',
          entityId: quoteId,
          entityName: clientName,
          changes: {
            etf_amount:        { from: null, to: calc.etfAmount },
            months_remaining:  { from: null, to: calc.monthsRemaining },
            posted_to_hubspot: { from: null, to: true },
          },
        });
      }
    } catch (e) {
      console.error('ETF HubSpot post failed:', e);
      setPostStatus('✗ ' + (e.message || 'Failed to post'));
    }
    setPostingNote(false);
  }

  // v7.14: Download PDF via PDFShift. Builds the letter HTML in signatureMode='none'
  // (visible signature lines, no SignWell tags), POSTs to /.netlify/functions/etfDocument
  // with render_pdf action, receives base64 PDF, triggers browser download.
  // v3.5.99.7.19: client now generates a UUID up front and passes it as letterId.
  // Server uses it as the etf_letters.id, so the row id matches the ref printed
  // on the PDF. Also passes the full set of input fields — previous versions
  // dropped mrr/term/dates from the payload, leaving the etf_letters columns
  // blank for every row created after v7.17.
  async function handleDownloadPdf() {
    setGeneratingPdf(true);
    setPdfStatus('');
    try {
      const letterId = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const letterRef = `ETF-${letterId.slice(0, 8)}`;
      const html = buildEtfLetterHTML({
        clientName, clientAddress,
        mrr: Number(mrr), term: parseInt(term, 10),
        contractStart: new Date(contractStart),
        terminationDate: new Date(terminationDate),
        ferrumInfo,
        signatureMode: 'none',
        contractReference,
        letterRef,
      });
      const safeBiz = (clientName || 'Client').replace(/[^a-z0-9]+/gi, '_').slice(0, 40);
      const filename = `ETF_Letter_${safeBiz}_${letterRef}.pdf`;
      const res = await fetch('/.netlify/functions/etfDocument', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'render_pdf',
          payload: {
            html, filename,
            letterId,                                                    // v7.19
            quoteId, entityName: clientName, etfAmount: calc.etfAmount,
            contractReference, hubDealId,
            // v7.19: full metadata so etf_letters columns aren't NULL
            mrr: Number(mrr),
            termMonths: parseInt(term, 10),
            contractStartDate: contractStart || null,
            terminationDate: terminationDate || null,
            monthsElapsed: calc.monthsElapsed,
            monthsRemaining: calc.monthsRemaining,
            clientAddress: clientAddress || null,
            clientContactName: clientContact || null,
            clientContactEmail: clientEmail || null,
            calculatedBy: profile?.full_name || profile?.email || null,
          },
        }),
      });
      const data = await res.json();
      if (!res.ok || !data?.pdf_base64) {
        throw new Error(data?.detail || data?.error || `HTTP ${res.status}`);
      }
      // Trigger browser download from base64
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${data.pdf_base64}`;
      link.download = data.filename || filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      if (data.hubspot?.ok) {
        setPdfStatus(`✓ PDF downloaded (${letterRef}) · Uploaded to HubSpot deal (file ${String(data.hubspot.fileId).slice(0, 8)}…)`);
      } else if (data.hubspot && !data.hubspot.ok) {
        setPdfStatus(`✓ PDF downloaded (${letterRef}) · ⚠ HubSpot upload failed: ${data.hubspot.error || 'unknown error'}`);
      } else {
        setPdfStatus(`✓ PDF downloaded (${letterRef})`);
      }
    } catch (e) {
      console.error('ETF PDF download failed:', e);
      setPdfStatus('✗ ' + (e.message || 'PDF failed'));
    }
    setGeneratingPdf(false);
  }

  // v7.14: Send via SignWell. Builds the letter HTML in signatureMode='two_party'
  // (text tags embedded for SignWell field overlay), POSTs to etfDocument with
  // send_signwell action + 2-party recipients (Ferrum signatory = current user,
  // Client signatory = recipientContact/recipientEmail from the quote).
  // v3.5.99.7.19: same UID + full-metadata payload treatment as handleDownloadPdf.
  async function handleSendViaSignwell() {
    if (!clientEmail || !clientContact) {
      setSignwellStatus('✗ Client name + email required');
      return;
    }
    if (!profile?.email || !profile?.full_name) {
      setSignwellStatus('✗ Your name + email required (check your profile)');
      return;
    }
    setSendingSignwell(true);
    setSignwellStatus('');
    try {
      const letterId = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const letterRef = `ETF-${letterId.slice(0, 8)}`;
      const html = buildEtfLetterHTML({
        clientName, clientAddress,
        mrr: Number(mrr), term: parseInt(term, 10),
        contractStart: new Date(contractStart),
        terminationDate: new Date(terminationDate),
        ferrumInfo,
        signatureMode: 'two_party',
        contractReference,
        letterRef,
      });
      const safeBiz = (clientName || 'Client').replace(/[^a-z0-9]+/gi, '_').slice(0, 40);
      const filename = `ETF_Letter_${safeBiz}_${letterRef}.pdf`;
      const res = await fetch('/.netlify/functions/etfDocument', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'send_signwell',
          payload: {
            html,
            filename,
            letterId,                                                    // v7.19
            docName: `Early Termination Notice — ${clientName}`,
            subject: `Action Required — Early Termination Acknowledgment · ${clientName}`,
            ferrumSignatoryName:  profile.full_name,
            ferrumSignatoryEmail: profile.email,
            clientSignatoryName:  clientContact,
            clientSignatoryEmail: clientEmail,
            quoteId,
            entityName: clientName,
            etfAmount: calc.etfAmount,
            calculatedBy: profile?.full_name || profile?.email || 'Ferrum IQ user',
            contractReference,
            hubDealId,
            // v7.19: full metadata so etf_letters columns aren't NULL
            mrr: Number(mrr),
            termMonths: parseInt(term, 10),
            contractStartDate: contractStart || null,
            terminationDate: terminationDate || null,
            monthsElapsed: calc.monthsElapsed,
            monthsRemaining: calc.monthsRemaining,
            clientAddress: clientAddress || null,
          },
        }),
      });
      const data = await res.json();
      if (!res.ok || !data?.documentId) {
        throw new Error(data?.detail || data?.error || `HTTP ${res.status}`);
      }
      if (data.hubspot?.ok) {
        setSignwellStatus(`✓ Sent (${letterRef}) — SignWell doc ${data.documentId.slice(0, 8)}… · Unsigned PDF uploaded to HubSpot deal`);
      } else if (data.hubspot && !data.hubspot.ok) {
        setSignwellStatus(`✓ Sent (${letterRef}) — SignWell doc ${data.documentId.slice(0, 8)}… · ⚠ HubSpot upload failed: ${data.hubspot.error || 'unknown error'}`);
      } else {
        setSignwellStatus(`✓ Sent (${letterRef}) — SignWell doc ${data.documentId.slice(0, 8)}…`);
      }
    } catch (e) {
      console.error('ETF SignWell send failed:', e);
      setSignwellStatus('✗ ' + (e.message || 'SignWell send failed'));
    }
    setSendingSignwell(false);
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 16,
      }}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'white', borderRadius: 8,
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          width: '100%', maxWidth: 640,
          maxHeight: 'calc(100vh - 32px)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}>
        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 18px', borderBottom:'1px solid #e5e7eb', flexShrink:0 }}>
          <h3 style={{ margin:0, fontSize:14, fontWeight:700, color:'#0f1e3c' }}>
            💰 Early Termination Fee Calculator
          </h3>
          <button type="button" onClick={onClose} title="Close (Esc)"
            style={{ background:'none', border:'none', fontSize:22, color:'#6b7280', cursor:'pointer', padding:0, lineHeight:1, width:24, height:24, display:'flex', alignItems:'center', justifyContent:'center' }}>×</button>
        </div>

        {/* Body */}
        <div style={{ padding:'14px 18px', overflowY:'auto', flex:1 }}>
          <EtfCalculatorBody
            clientName={clientName} setClientName={setClientName}
            clientAddress={clientAddress} setClientAddress={setClientAddress}
            clientContact={clientContact} setClientContact={setClientContact}
            clientEmail={clientEmail} setClientEmail={setClientEmail}
            contractReference={contractReference} setContractReference={setContractReference}
            mrr={mrr} setMrr={setMrr}
            term={term} setTerm={setTerm}
            contractStart={contractStart} setContractStart={setContractStart}
            terminationDate={terminationDate} setTerminationDate={setTerminationDate}
            calc={calc}
            canCalculate={canCalculate}
            onGenerateLetter={handleGenerateLetter}
            onPostToHubspot={hubDealId ? handlePostToHubspot : null}
            onDownloadPdf={handleDownloadPdf}
            onSendViaSignwell={(quoteId && clientEmail) ? handleSendViaSignwell : null}
            postingNote={postingNote}
            postStatus={postStatus}
            generatingPdf={generatingPdf}
            pdfStatus={pdfStatus}
            sendingSignwell={sendingSignwell}
            signwellStatus={signwellStatus}
            showContactFields={!!quoteId}
            hintMessage={quoteId && hintLoaded && !contractStart
              ? 'No signature event found for this quote — enter the contract start date manually.'
              : (quoteId && contractStart ? 'Contract start prefilled from SIGNATURE_COMPLETED activity log.' : null)}
          />
        </div>
      </div>
    </div>
  );
}

// Shared body — also used by ETFCalculatorPage (standalone). Pure
// presentational, all state lifted to caller.
export function EtfCalculatorBody({
  clientName, setClientName,
  clientAddress, setClientAddress,
  clientContact, setClientContact,    // v7.14: contact name (for SignWell signer)
  clientEmail, setClientEmail,        // v7.14: contact email (for SignWell signer)
  contractReference, setContractReference,  // v7.14.2: human-readable contract id
  mrr, setMrr,
  term, setTerm,
  contractStart, setContractStart,
  terminationDate, setTerminationDate,
  calc,
  canCalculate,
  onGenerateLetter,
  onPostToHubspot,
  onDownloadPdf,                       // v7.14: PDFShift handler
  onSendViaSignwell,                   // v7.14: SignWell handler (null on standalone page)
  postingNote,
  postStatus,
  generatingPdf = false,               // v7.14
  pdfStatus = '',                      // v7.14
  sendingSignwell = false,             // v7.14
  signwellStatus = '',                 // v7.14
  showContactFields = false,           // v7.14: only show Contact Name/Email when SignWell is available
  hintMessage,
}) {
  const { fmtUSD } = formatters;
  return (
    <>
      {/* v7.14.2: Contract Reference — full-width and visually prominent.
          When a client has multiple active contracts with us (Managed IT
          + Voice + Bundle), this is the field that identifies WHICH one is
          being terminated. Auto-fills from quote_number when opened from a
          per-quote modal; standalone leaves it blank for the rep to enter. */}
      <div style={{ marginBottom: 12 }}>
        <Fld label="Contract Reference" sub="quote number, MSA reference, or other identifier — appears prominently on the letter">
          <TI value={contractReference} onChange={setContractReference} placeholder="e.g. QT-2024-0042 or Managed IT MSA dated 4/30/2024" />
        </Fld>
      </div>

      {/* Inputs */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:14 }}>
        <Fld label="Client Name">
          <TI value={clientName} onChange={setClientName} placeholder="e.g. Mundo Auto" />
        </Fld>
        <Fld label="MRR ($/month)">
          <TI value={mrr} onChange={setMrr} placeholder="3500" type="number" prefix="$" />
        </Fld>
        <Fld label="Contract Term (months)">
          <TI value={term} onChange={setTerm} type="number" />
        </Fld>
        <Fld label="Termination Effective Date">
          <TI value={terminationDate} onChange={setTerminationDate} type="date" />
        </Fld>
        <Fld label="Contract Start Date">
          <TI value={contractStart} onChange={setContractStart} type="date" />
        </Fld>
        <Fld label="Client Address (optional)" sub="appears on the letter">
          <TI value={clientAddress} onChange={setClientAddress} placeholder="1234 Main St, City, ST 12345" />
        </Fld>
        {showContactFields && (
          <>
            <Fld label="Client Contact Name" sub="signs as Party 2 on SignWell">
              <TI value={clientContact} onChange={setClientContact} placeholder="Jane Smith" />
            </Fld>
            <Fld label="Client Contact Email" sub="receives SignWell signing request">
              <TI value={clientEmail} onChange={setClientEmail} placeholder="jane@example.com" type="email" />
            </Fld>
          </>
        )}
      </div>

      {hintMessage && (
        <div style={{ fontSize:v3.font.size.small, color:v3.color.muted, marginBottom:10, fontStyle:'italic' }}>{hintMessage}</div>
      )}

      {/* Live result */}
      <div style={{ background: canCalculate ? v3.color.inkBg : v3.color.cardAlt, border:`1px solid ${canCalculate ? v3.color.inkBorder : v3.color.border}`, borderRadius:v3.radius.cardLarge, padding:'14px 16px', marginBottom:12 }}>
        <div style={{ fontSize:v3.font.size.micro, fontWeight:v3.font.weight.semibold, color:v3.color.ink, textTransform:'uppercase', letterSpacing:v3.font.letterSpacing.uppercase, marginBottom:8 }}>Calculation</div>
        {canCalculate ? (
          <>
            <div style={{ display:'grid', gridTemplateColumns:'1fr auto', gap:'6px 12px', fontSize:v3.font.size.label, color:v3.color.bodySoft, marginBottom:12 }}>
              <span>Months elapsed (complete)</span><span style={{ fontWeight:v3.font.weight.semibold, fontFamily:v3.font.mono }}>{calc.monthsElapsed}</span>
              <span>Months remaining</span><span style={{ fontWeight:v3.font.weight.semibold, fontFamily:v3.font.mono }}>{calc.monthsRemaining}</span>
              <span>MRR × months remaining</span><span style={{ fontWeight:v3.font.weight.semibold, fontFamily:v3.font.mono }}>{fmtUSD(mrr)} × {calc.monthsRemaining}</span>
            </div>
            <div style={{ textAlign:'center', padding:'12px', background:v3.color.card, borderRadius:v3.radius.card, border:`1px solid ${v3.color.inkBorder}` }}>
              <div style={{ fontSize:v3.font.size.micro, fontWeight:v3.font.weight.semibold, color:v3.color.ink, textTransform:'uppercase', letterSpacing:v3.font.letterSpacing.uppercase }}>Early Termination Fee</div>
              <div style={{ fontSize:v3.font.size.money, fontWeight:v3.font.weight.semibold, color:v3.color.body, marginTop:4, fontFamily:v3.font.mono, letterSpacing:v3.font.letterSpacing.tighter }}>{fmtUSD(calc.etfAmount)}</div>
            </div>
          </>
        ) : calc.warnings.length === 0 ? (
          // No warnings + can't calc → fields are missing. Show the generic prompt.
          <div style={{ fontSize:v3.font.size.label, color:v3.color.muted }}>
            Fill in MRR, term, contract start, and termination date to compute.
          </div>
        ) : null /* warnings will render below — no fallback prompt needed */}
        {/* v3.5.99.7.18.2: warnings now display whenever they exist, not just
            when canCalculate is true. Prior logic hid them in the very case
            they're most needed — when warnings are why the buttons are
            disabled. Most common trigger: termination date equals or exceeds
            contract end date ("No ETF is owed"). */}
        {calc.warnings.length > 0 && (
          <div style={{ marginTop: canCalculate ? 8 : 0, padding:'6px 10px', background:v3.color.status.amberBg, border:`1px solid ${v3.color.status.amberText}33`, borderRadius:v3.radius.input, fontSize:v3.font.size.small, color:v3.color.status.amberText }}>
            ⚠ {calc.warnings.join(' · ')}
          </div>
        )}
      </div>

      {/* Actions */}
      <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
        <button type="button" onClick={onGenerateLetter} disabled={!canCalculate || calc.etfAmount === 0}
          style={btnStyle(v3.color.muted, 'white', !canCalculate || calc.etfAmount === 0)}>
          📄 Preview (HTML)
        </button>
        {onDownloadPdf && (
          <button type="button" onClick={onDownloadPdf} disabled={!canCalculate || calc.etfAmount === 0 || generatingPdf}
            style={btnStyle(v3.color.ink, 'white', !canCalculate || calc.etfAmount === 0 || generatingPdf)}>
            {generatingPdf ? '⏳ Rendering...' : '📥 Download PDF'}
          </button>
        )}
        {onSendViaSignwell && (
          <button type="button" onClick={onSendViaSignwell} disabled={!canCalculate || calc.etfAmount === 0 || sendingSignwell}
            style={btnStyle(v3.color.navy, 'white', !canCalculate || calc.etfAmount === 0 || sendingSignwell)}>
            {sendingSignwell ? '⏳ Sending...' : '✍ Send via SignWell'}
          </button>
        )}
        {onPostToHubspot && (
          <button type="button" onClick={onPostToHubspot} disabled={!canCalculate || postingNote}
            style={btnStyle(v3.color.inkSoft, 'white', !canCalculate || postingNote)}>
            {postingNote ? '⏳ Posting...' : '📤 Post to HubSpot Deal'}
          </button>
        )}
      </div>

      {/* Status messages — one row each so they don't overlap */}
      <div style={{ marginTop:8, display:'flex', flexDirection:'column', gap:3 }}>
        {pdfStatus      && <span style={{ fontSize:v3.font.size.small, color: pdfStatus.startsWith('✓')      ? v3.color.status.greenText : v3.color.status.redText }}>{pdfStatus}</span>}
        {signwellStatus && <span style={{ fontSize:v3.font.size.small, color: signwellStatus.startsWith('✓') ? v3.color.status.greenText : v3.color.status.redText }}>{signwellStatus}</span>}
        {postStatus     && <span style={{ fontSize:v3.font.size.small, color: postStatus.startsWith('✓')     ? v3.color.status.greenText : v3.color.status.redText }}>{postStatus}</span>}
      </div>
    </>
  );
}

// Small form-field wrapper — v3 design system (v3.7.7)
function Fld({ label, sub, children }) {
  return (
    <div>
      <label style={{ display:'block', fontSize:v3.font.size.label, fontWeight:v3.font.weight.semibold, color:v3.color.bodySoft, marginBottom:4 }}>
        {label}{sub && <span style={{ fontWeight:v3.font.weight.normal, color:v3.color.mutedSoft, fontSize:v3.font.size.small }}> — {sub}</span>}
      </label>
      {children}
    </div>
  );
}

function TI({ value, onChange, type='text', placeholder='', prefix='' }) {
  return (
    <div style={{ position:'relative' }}>
      {prefix && <span style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', fontSize:v3.font.size.body, color:v3.color.mutedSoft }}>{prefix}</span>}
      <input type={type} value={value} placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        style={{ width:'100%', boxSizing:'border-box', padding: prefix ? '10px 12px 10px 22px' : v3.space.inputPad, border:`1px solid ${v3.color.border}`, borderRadius:v3.radius.input, fontSize:v3.font.size.body, fontFamily: type === 'number' ? v3.font.mono : v3.font.family, color:v3.color.body, background:v3.color.card, fontWeight: type === 'number' ? v3.font.weight.semibold : v3.font.weight.normal, outline:'none' }} />
    </div>
  );
}

function btnStyle(bg, color, disabled) {
  return {
    padding:'9px 14px',
    background: disabled ? v3.color.mutedSoft : bg,
    color, border:'none', borderRadius:v3.radius.input,
    fontSize:v3.font.size.label, fontWeight:v3.font.weight.semibold,
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}
